import {
  CUSTOM_THEME_KEYS,
  DEFAULT_THEME,
  THEMES,
  type CustomTheme,
  type Theme,
} from '@moussenaires/shared';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { publicSettingsApi } from '../api/settings.js';

const STORAGE_KEY = 'moussenaires.theme';
const CUSTOM_STORAGE_KEY = 'moussenaires.theme.custom';

const CSS_VAR_BY_KEY: Record<(typeof CUSTOM_THEME_KEYS)[number], string> = {
  bg: '--bg',
  surface: '--surface',
  text: '--text',
  accent: '--accent',
  accentContrast: '--accent-contrast',
};

interface ThemeContextValue {
  theme: Theme;
  customTheme: CustomTheme | null;
  /** Applies immediately and persists locally; callers also push it to the server for logged-in users. */
  setTheme: (theme: Theme, customTheme?: CustomTheme | null) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Falls back to the OS/browser's own light-vs-dark preference, not the
 * hardcoded built-in default — matching the inline script in `index.html`
 * that stamps `data-theme` before the first paint. If this ever disagreed
 * with that script, the very first render-after-hydration would immediately
 * flip the theme back, turning the flash the inline script exists to
 * prevent into a slightly later one instead.
 */
function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (THEMES as readonly string[]).includes(stored)) return stored as Theme;
    if (window.matchMedia?.('(prefers-color-scheme: light)').matches) return 'light';
  } catch {
    // ignore
  }
  return DEFAULT_THEME;
}

function readStoredCustomTheme(): CustomTheme | null {
  try {
    const stored = localStorage.getItem(CUSTOM_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as CustomTheme) : null;
  } catch {
    return null;
  }
}

/** Distinguishes "never touched their theme" from "picked the same theme the built-in default happens to be". */
function hasExplicitPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== null;
  } catch {
    return false;
  }
}

function applyThemeToDocument(theme: Theme, customTheme: CustomTheme | null): void {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme);
  for (const key of CUSTOM_THEME_KEYS) {
    const cssVar = CSS_VAR_BY_KEY[key];
    const value = theme === 'custom' ? customTheme?.[key] : undefined;
    if (value) root.style.setProperty(cssVar, value);
    else root.style.removeProperty(cssVar);
  }
}

/**
 * Applies the active theme to `<html data-theme>` and, for `custom`, as
 * inline CSS variable overrides. Works for anonymous visitors too (the
 * public roster page): it just falls back to `localStorage` instead of the
 * user's saved preference — and, when there's nothing in `localStorage`
 * either (nobody in this browser has ever touched their theme), to the
 * admin-configured site default rather than the hardcoded built-in one.
 */
export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [customTheme, setCustomThemeState] = useState<CustomTheme | null>(readStoredCustomTheme);

  useEffect(() => {
    if (hasExplicitPreference()) return;
    let cancelled = false;
    publicSettingsApi
      .get()
      .then((settings) => {
        if (cancelled) return;
        setThemeState(settings.defaultTheme);
        setCustomThemeState(settings.defaultThemeCustom);
      })
      .catch(() => {
        // Offline, or the request failed for some other reason — the
        // built-in default this already rendered with is a fine fallback.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    applyThemeToDocument(theme, customTheme);
  }, [theme, customTheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      customTheme,
      setTheme: (nextTheme, nextCustomTheme) => {
        setThemeState(nextTheme);
        if (nextCustomTheme !== undefined) setCustomThemeState(nextCustomTheme);
        try {
          localStorage.setItem(STORAGE_KEY, nextTheme);
          if (nextCustomTheme)
            localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(nextCustomTheme));
        } catch {
          // ignore
        }
      },
    }),
    [theme, customTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
